import type { ReactNode } from 'react'
import { money, riskTone } from '../lib/format'

export function Severity({ value }: { value: string }) {
  const label: Record<string, string> = { low: 'ต่ำ', medium: 'ปานกลาง', high: 'สูง', critical: 'วิกฤต' }
  return (
    <span className={`sev sev-${value}`}>
      <i className="sev-dot" />
      {label[value] ?? value}
    </span>
  )
}

export function Money({ satang, compact = false }: { satang: number; compact?: boolean }) {
  return <span className="tnum">{money(satang, { compact })}</span>
}

export function RiskBar({ score }: { score: number }) {
  const tone = score >= 60 ? 'var(--color-amber)' : score >= 35 ? 'var(--color-gold)' : 'var(--color-emerald)'
  return (
    <span className="row" style={{ gap: 10 }}>
      <span className="bar" style={{ width: 54 }}><i style={{ width: `${score}%`, background: tone }} /></span>
      <span className={`tnum ${riskTone(score)}`} style={{ minWidth: 22 }}>{score}</span>
    </span>
  )
}

export function Stat({ label, value, unit, foot }: { label: string; value: ReactNode; unit?: string; foot?: ReactNode }) {
  return (
    <div className="card card-pad rise">
      <div className="card-label">{label}</div>
      <div className="stat-value">{value}{unit ? <span className="stat-unit">{unit}</span> : null}</div>
      {foot ? <div className="stat-foot">{foot}</div> : null}
    </div>
  )
}

export function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      <p style={{ fontSize: 12.5 }}>{body}</p>
    </div>
  )
}

export function Skel({ h = 16, w = '100%' }: { h?: number; w?: number | string }) {
  return <div className="skel" style={{ height: h, width: w }} />
}

export function PageHead({ title, sub, actions }: { title: string; sub?: string; actions?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <div className="eyebrow" style={{ marginBottom: 12 }}>AUDIT AI · การตรวจสอบ</div>
        <h1>{title}</h1>
        {sub ? <div className="sub">{sub}</div> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </div>
  )
}

const paths: Record<string, string> = {
  dashboard: 'M3 3h7v7H3zM14 3h7v4h-7zM14 10h7v11h-7zM3 13h7v8H3z',
  findings: 'M12 3l9 16H3L12 3zm0 6v4m0 3h.01',
  documents: 'M6 3h8l4 4v14H6zM14 3v4h4',
  engagements: 'M4 6h16M4 12h16M4 18h10',
  trail: 'M12 8v4l3 2M21 12a9 9 0 11-9-9 9 9 0 019 9z',
  sun: 'M12 4v2M12 18v2M4 12h2M18 12h2M6.3 6.3l1.4 1.4M16.3 16.3l1.4 1.4M6.3 17.7l1.4-1.4M16.3 7.7l1.4-1.4M12 8a4 4 0 100 8 4 4 0 000-8z',
  moon: 'M20 14.5A8 8 0 019.5 4a7.5 7.5 0 1010.5 10.5z',
  lock: 'M6 11h12v9H6zM9 11V8a3 3 0 016 0v3',
  cases: 'M4 7h16v13H4zM4 7l4-3h8l4 3M9 12h6',
  billing: 'M2 7h20v12H2zM2 11h20M6 15h4',
  settings: 'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.7 1.7 0 00.3 1.9l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.9-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1-1.6 1.7 1.7 0 00-1.9.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.9 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.6-1 1.7 1.7 0 00-.3-1.9l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.9.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.9-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.9V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z',
  search: 'M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.3-4.3',
  board: 'M4 4h6v16H4zM14 4h6v9h-6zM14 17h6v3h-6z',
  shield: 'M12 3l8 3v6c0 5-3.5 7.5-8 9-4.5-1.5-8-4-8-9V6z',
  bell: 'M18 8a6 6 0 10-12 0c0 7-3 8-3 8h18s-3-1-3-8M10.3 21a2 2 0 003.4 0',
}

export function Icon({ name, size = 17 }: { name: keyof typeof paths | string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={paths[name] ?? paths.dashboard} />
    </svg>
  )
}
